import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Reveal, RevealWords, Overline } from "./shared";

function Counter({ to, suffix = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [val, setVal] = useState(0);

  useEffect(() => {
    if (!inView) return;
    let raf;
    const start = performance.now();
    const dur = 1600;
    const tick = (now) => {
      const p = Math.min((now - start) / dur, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.floor(eased * to));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, to]);

  return (
    <span ref={ref} className="text-gradient font-display text-5xl font-bold md:text-6xl">
      {val}
      {suffix}
    </span>
  );
}

const stats = [
  { to: 500, suffix: "+", label: "Projects delivered" },
  { to: 99, suffix: "%", label: "Uptime guaranteed" },
  { to: 24, suffix: "/7", label: "Support coverage" },
  { to: 15, suffix: "+", label: "Years of expertise" },
];

const reasons = [
  "Vendor-agnostic advice focused purely on what's right for you",
  "One accountable team across hardware, cloud, security and design",
  "Proactive monitoring that prevents problems before they happen",
  "Transparent pricing with no lock-in surprises",
];

export default function WhyChooseUs() {
  return (
    <section
      id="why-us"
      data-testid="why-us-section"
      className="relative overflow-hidden py-16 md:py-24"
    >
      <div className="glow-blob left-1/2 top-0 h-72 w-72 -translate-x-1/2 bg-[#00f0ff]" />
      <div className="mx-auto max-w-7xl px-6">
        <div className="mb-16 max-w-2xl">
          <Reveal>
            <Overline>Why Gabicom</Overline>
          </Reveal>
          <RevealWords
            text="Built on trust, engineered for results"
            className="font-display mt-5 text-4xl font-semibold leading-tight text-white md:text-5xl"
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {stats.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.08}>
              <div
                data-testid={`stat-${i}`}
                className="glass rounded-3xl p-8 text-center"
              >
                <Counter to={s.to} suffix={s.suffix} />
                <p className="mt-3 font-mono-tech text-xs uppercase tracking-widest text-[#a0aec0]">
                  {s.label}
                </p>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="mt-8 grid gap-4 sm:grid-cols-2">
          {reasons.map((r, i) => (
            <Reveal key={r} delay={i * 0.06}>
              <motion.div
                whileHover={{ x: 6 }}
                className="border-gradient flex items-start gap-4 rounded-2xl p-6"
              >
                <span className="mt-1 h-2.5 w-2.5 flex-shrink-0 rounded-full bg-gradient-to-r from-[#00f0ff] to-[#b026ff]" />
                <p className="text-[#f8f9fa]">{r}</p>
              </motion.div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
